import { ActionExecutionDto, ActionExecutionStatus } from '@ampsec/platform-client';
import { ColDef } from 'ag-grid-community';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
    AGENT_COLUMN,
    DURATION,
    EXECUTED_AT,
    STATUS,
    USER_COLUMN,
    ACTION_EXECUTION_INPUT_COLUMN,
    ACTION_EXECUTION_OUTPUT_COLUMN,
} from 'Core-utils/constants/constants';
import {
    formatExecutionDate,
    formatExecutionDuration,
    formatExecutionPayloadPreview,
    getExecutionStatusBadgeClass,
} from 'Core-utils/helpers/customActionExecutionHelpers';
import { DEFAULT_WORKFLOW_ROUTE } from 'Core-utils/routes';
import { COLUMN_MIN_WIDTHS } from './AgGridCustomActionExecutions.config';

/** Row shape for audit grid (extends platform ActionExecutionDto with joined display fields). */
export type ActionExecutionAuditRow = ActionExecutionDto & {
    flowSpecId?: string | null;
    flowSpecDisplayValue?: string | null;
    userId?: string | null;
    userDisplayValue?: string | null;
};

export interface GetCustomActionExecutionsColumnsParams {
    onOpenPayload: (title: string, payload: unknown) => void;
}

export function getCustomActionExecutionsColumns(
    params: GetCustomActionExecutionsColumnsParams,
): ColDef<ActionExecutionAuditRow>[] {
    const { onOpenPayload } = params;

    const renderPayloadCell = (columnTitle: string) => (cellParams: { value?: unknown }) => {
        const preview = formatExecutionPayloadPreview(cellParams.value);
        if (preview === '—') {
            return preview;
        }
        return (
            <span
                className="custom-action-executions__payload-cell"
                role="button"
                tabIndex={0}
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenPayload(columnTitle, cellParams.value);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onOpenPayload(columnTitle, cellParams.value);
                    }
                }}
            >
                {preview}
            </span>
        );
    };

    return [
        {
            field: 'executionTime',
            headerName: EXECUTED_AT,
            colId: 'executionTime',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
            cellRenderer: (cellParams: { value?: string | null }) => formatExecutionDate(cellParams.value),
        },
        {
            field: 'status',
            headerName: STATUS,
            colId: 'status',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.MINI_TEXT,
            cellRenderer: (cellParams: { value?: ActionExecutionStatus }) => {
                const status = cellParams.value as ActionExecutionStatus;
                return <Badge className={getExecutionStatusBadgeClass(status)}>{status ?? '—'}</Badge>;
            },
        },
        {
            headerName: AGENT_COLUMN,
            colId: 'flowSpecDisplayValue',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.MEDIUM_TEXT,
            valueGetter: (cellParams) => cellParams.data?.flowSpecDisplayValue,
            cellRenderer: (cellParams: { data?: ActionExecutionAuditRow; value?: string | null }) => {
                const flowSpecId = cellParams.data?.flowSpecId;
                const label = cellParams.value || flowSpecId || '—';
                if (!flowSpecId) {
                    return label;
                }
                return (
                    <Link
                        to={`${DEFAULT_WORKFLOW_ROUTE}/create-engagement/${flowSpecId}`}
                        className="text-decoration-none ag-engagement-flow__link text-truncate bodyFont d-block"
                        title={typeof label === 'string' ? label : undefined}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {label}
                    </Link>
                );
            },
        },
        {
            headerName: USER_COLUMN,
            colId: 'userDisplayValue',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
            valueGetter: (cellParams) => cellParams.data?.userDisplayValue,
            cellRenderer: (cellParams: { value?: string | null }) => cellParams.value || '—',
        },
        {
            field: 'executionDuration',
            headerName: DURATION,
            colId: 'executionDuration',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.MINI_TEXT,
            cellRenderer: (cellParams: { value?: number | null }) => formatExecutionDuration(cellParams.value),
        },
        {
            field: 'input',
            headerName: ACTION_EXECUTION_INPUT_COLUMN,
            colId: 'input',
            flex: 1.5,
            minWidth: COLUMN_MIN_WIDTHS.LONG_TEXT,
            cellRenderer: renderPayloadCell(ACTION_EXECUTION_INPUT_COLUMN),
        },
        {
            field: 'output',
            headerName: ACTION_EXECUTION_OUTPUT_COLUMN,
            colId: 'output',
            flex: 1.5,
            minWidth: COLUMN_MIN_WIDTHS.LONG_TEXT,
            cellRenderer: renderPayloadCell(ACTION_EXECUTION_OUTPUT_COLUMN),
        },
        {
            field: 'flowStateId',
            headerName: 'Flow State ID',
            colId: 'flowStateId',
            flex: 0.5,
            minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
            cellRenderer: (cellParams: { value?: string | null }) => cellParams.value || '—',
        },
    ];
}
